import { Component, computed } from "vue";
import { useI18n } from "vue-i18n";
import {
    createBlock,
    linkTextField,
    headingField,
    htmlField,
    buttonField,
    arrayField,
    textField,
    descriptiveImageField,
} from "./blockBuilder";

import type { BlockDef } from "./block.interfaces";
import { useDefaultColors } from "./useDefaultColors";
import {
    SimpleCentered,
    SplitWithImageLeft,
    SplitWithImageRight,
    SplitWithScreenshot,
    WithAlignedImageLeft,
    WithAlignedImageRight,
    WithAppScreenshot,
    WithOffsetImage,
} from "@/views/blocks/hero/hero";
import type { HeroBaseProps } from "@/views/blocks/hero/hero.interfaces";
import participantImageSrc1 from "@/views/blocks/hero/shared/socialproof/images/ellipse1.png";
import participantImageSrc2 from "@/views/blocks/hero/shared/socialproof/images/ellipse2.png";
import participantImageSrc3 from "@/views/blocks/hero/shared/socialproof/images/ellipse3.png";
import participantImageSrc4 from "@/views/blocks/hero/shared/socialproof/images/ellipse4.png";
import participantImageSrc5 from "@/views/blocks/hero/shared/socialproof/images/ellipse5.png";
import heroImage from "@/views/blocks/hero/shared/images/image.jpg";
import screenshotImage from "@/views/blocks/hero/shared/images/page-left.png";
import appScreenshotImage from "@/views/blocks/hero/shared/images/page-top.png";
import { useFieldConfig } from "./useFieldConfig";

export function useHeros() {
    const { t } = useI18n();
    const { colorDefaults } = useDefaultColors();
    const { translations } = useFieldConfig();

    function createBaseHeroBlock({
        component,
        typeId,
        name,
        extendBlock,
    }: {
        component: Component;
        typeId: string;
        name: string;
        extendBlock?: (block: ReturnType<typeof createBlock>) => ReturnType<typeof createBlock>;
    }): BlockDef {
        let block = createBlock<Omit<HeroBaseProps, "image">>({
            typeId,
            type: "hero",
            name: name,
            component,
        })
            .withField(
                "socialProofText",
                textField(
                    translations.value.socialProofText,
                    "9000+ Participants in 2 years",
                    translations.value.text
                )
            )
            .withArrayField(
                "socialProofImages",
                arrayField(
                    translations.value.image,
                    "socialProofImages",
                    {
                        src: { label: translations.value.src, type: "image" },
                        alt: { label: translations.value.alt, type: "text" },
                    },
                    [
                        { src: participantImageSrc1, alt: "Participant 1" },
                        { src: participantImageSrc2, alt: "Participant 2" },
                        { src: participantImageSrc3, alt: "Participant 3" },
                        { src: participantImageSrc4, alt: "Participant 4" },
                        { src: participantImageSrc5, alt: "Participant 5" },
                    ],
                    {
                        src: participantImageSrc1,
                        alt: "Participant X",
                    }
                )
            )
            .withField(
                "tag",
                linkTextField(
                    translations.value.tag,
                    {
                        text: "Easytools quick start",
                        label: "Read more",
                        href: "https://www.easy.tools/changelog/easytools-quick-start",
                    },
                    {
                        text: translations.value.text,
                        label: translations.value.label,
                        href: translations.value.href,
                    }
                )
            )
            .withField(
                "title",
                headingField(translations.value.title, "Transform Business with Easytools", "h1", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "paragraph",
                htmlField(
                    translations.value.paragraph,
                    "Streamline your workflow, boost productivity, and make your business operations more efficient with our comprehensive suite of tools designed for modern enterprises.",
                    translations.value.text
                )
            )
            .withArrayField(
                "details",
                arrayField(
                    translations.value.detail,
                    "details",
                    {
                        title: { label: translations.value.title },
                        description: { label: translations.value.description },
                    },
                    [
                        {
                            title: "24.05",
                            description: "Start",
                        },
                        {
                            title: "4500+",
                            description: "Graduates",
                        },
                        {
                            title: "6+1",
                            description: "Weeks",
                        },
                    ],
                    {
                        title: "New item",
                        description: "New description",
                    }
                )
            )
            .withField(
                "primaryButton",
                buttonField(
                    translations.value.primaryButton,
                    {
                        label: "Get started",
                        href: "https://cart.easy.tools/register",
                    },
                    {
                        label: translations.value.label,
                        href: translations.value.href,
                    }
                )
            )
            .withField(
                "secondaryButton",
                buttonField(
                    translations.value.secondaryButton,
                    {
                        label: translations.value.label,
                        href: translations.value.href,
                    },
                    {
                        label: translations.value.label,
                        href: translations.value.href,
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
                colorDefaults.value.border,
            ]);

        if (extendBlock) {
            block = extendBlock(block);
        }

        return block.build();
    }

    const simpleCenteredBlock = computed<BlockDef>(() =>
        createBaseHeroBlock({
            typeId: "SimpleCentered",
            name: t("page.blocks.hero.simpleCentered"),
            component: SimpleCentered,
        })
    );

    const splitWithImageLeftBlock = computed<BlockDef>(() =>
        createBaseHeroBlock({
            component: SplitWithImageLeft,
            typeId: "SplitWithImageLeft",
            name: t("page.blocks.hero.splitWithImageLeft"),
            extendBlock: (block) =>
                block.withField(
                    "image",
                    descriptiveImageField(
                        translations.value.src,
                        {
                            src: heroImage,
                            alt: "Hero image",
                        },
                        {
                            src: translations.value.src,
                            alt: translations.value.alt,
                        }
                    )
                ),
        })
    );

    const splitWithImageRightBlock = computed<BlockDef>(() =>
        createBaseHeroBlock({
            component: SplitWithImageRight,
            typeId: "SplitWithImageRight",
            name: t("page.blocks.hero.splitWithImageRight"),
            extendBlock: (block) =>
                block.withField(
                    "image",
                    descriptiveImageField(
                        translations.value.src,
                        {
                            src: heroImage,
                            alt: "Hero image",
                        },
                        {
                            src: translations.value.src,
                            alt: translations.value.alt,
                        }
                    )
                ),
        })
    );

    const splitWithScreenshotBlock = computed<BlockDef>(() =>
        createBaseHeroBlock({
            typeId: "SplitWithScreenshot",
            name: t("page.blocks.hero.splitWithScreenshot"),
            component: SplitWithScreenshot,
            extendBlock: (block) =>
                block.withField(
                    "image",
                    descriptiveImageField(
                        translations.value.src,
                        { src: screenshotImage, alt: "Hero image" },
                        {
                            src: translations.value.src,
                            alt: translations.value.alt,
                        }
                    )
                ),
        })
    );

    const withAppScreenshotBlock = computed<BlockDef>(() =>
        createBaseHeroBlock({
            typeId: "WithAppScreenshot",
            name: t("page.blocks.hero.withAppScreenshot"),
            component: WithAppScreenshot,
            extendBlock: (block) =>
                block.withField(
                    "image",
                    descriptiveImageField(
                        translations.value.src,
                        { src: appScreenshotImage, alt: "Hero image" },
                        {
                            src: translations.value.src,
                            alt: translations.value.alt,
                        }
                    )
                ),
        })
    );

    const withAlignedImageLeftBlock = computed<BlockDef>(() =>
        createBaseHeroBlock({
            typeId: "WithAlignedImageLeft",
            name: t("page.blocks.hero.withAlignedImageLeft"),
            component: WithAlignedImageLeft,
            extendBlock: (block) =>
                block.withField(
                    "image",
                    descriptiveImageField(
                        translations.value.src,
                        { src: heroImage, alt: "Hero image" },
                        {
                            src: translations.value.src,
                            alt: translations.value.alt,
                        }
                    )
                ),
        })
    );

    const withAlignedImageRightBlock = computed<BlockDef>(() =>
        createBaseHeroBlock({
            typeId: "WithAlignedImageRight",
            name: t("page.blocks.hero.withAlignedImageRight"),
            component: WithAlignedImageRight,
            extendBlock: (block) =>
                block.withField(
                    "image",
                    descriptiveImageField(
                        translations.value.src,
                        { src: heroImage, alt: "Hero image" },
                        {
                            src: translations.value.src,
                            alt: translations.value.alt,
                        }
                    )
                ),
        })
    );

    const withOffsetImageBlock = computed<BlockDef>(() =>
        createBaseHeroBlock({
            typeId: "WithOffsetImage",
            name: t("page.blocks.hero.withOffsetImage"),
            component: WithOffsetImage,
            extendBlock: (block) =>
                block.withField(
                    "image",
                    descriptiveImageField(
                        translations.value.src,
                        { src: heroImage, alt: "Hero image" },
                        {
                            src: translations.value.src,
                            alt: translations.value.alt,
                        }
                    )
                ),
        })
    );

    return {
        blocks: computed(() => [
            simpleCenteredBlock.value,
            splitWithImageLeftBlock.value,
            splitWithImageRightBlock.value,
            splitWithScreenshotBlock.value,
            withAppScreenshotBlock.value,
            withAlignedImageLeftBlock.value,
            withAlignedImageRightBlock.value,
            withOffsetImageBlock.value,
        ]),
    };
}
